import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { submitLesson } from "@/lib/api";

const PRO_SHOP_IMG =
  "https://images.pexels.com/photos/1325735/pexels-photo-1325735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=940";

const merchandise = [
  {
    title: "Golf Equipment",
    description: "A selection of golf equipment, accessories and everyday items available from our Pro Shop.",
    image:
      "https://images.unsplash.com/photo-1535132011086-b8818f016104?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Golf Essentials",
    description: "Browse a range of golfing essentials to help you enjoy your time on the course.",
    image:
      "https://images.unsplash.com/photo-1592919505780-303950717480?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Friendly Service",
    description: "Our team is happy to help with any questions and assist you during your visit.",
    image:
      "https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?auto=format&fit=crop&w=900&q=80",
  },
];

export default function ProShop() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitLesson(form);
      toast.success("Lesson inquiry received. Our PGA team will reach out soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Unable to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="pro-shop"
      data-testid="pro-shop-section"
      className="bg-[#FAFAFA] py-24 md:py-32 lg:py-40 border-y border-[#CBA153]/15"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <span className="section-eyebrow">02 · Pro Shop</span>
          <h2 className="font-serif-luxury text-4xl md:text-5xl lg:text-6xl text-[#0F4C3A] mt-5 leading-[1.05] tracking-tight">
            The Gower Golf Club
            <span className="block italic text-gold-metallic">Pro Shop</span>
          </h2>
          <p className="text-[#4A4A4A] mt-6 leading-relaxed">
            Located within the clubhouse, our Pro Shop offers a range of golf accessories, essentials and items for
            members and visitors alike.
          </p>
        </div>

        {/* Merchandise grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
          {merchandise.map((m, i) => (
            <article
              key={m.title}
              data-testid={`merch-card-${i}`}
              className="bg-white border border-[#CBA153]/25 hover-lift"
            >
              <div className="image-frame aspect-[4/5] overflow-hidden">
                <img src={m.image} alt={m.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-7">
                <p className="section-eyebrow text-[10px]">Pro Shop</p>
                <h3 className="font-serif-luxury text-2xl text-[#0F4C3A] mt-2">{m.title}</h3>
                <p className="text-sm text-[#4A4A4A] mt-3 leading-relaxed">{m.description}</p>
                <div className="gold-rule mt-5" />
              </div>
            </article>
          ))}
        </div>

        {/* CTA + lessons split */}
        <div className="hidden mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">
          <div className="lg:col-span-6 image-frame border border-[#CBA153]/25">
            <img src={PRO_SHOP_IMG} alt="Custom fitting suite with PGA Professional at The Gower Golf Club" className="w-full h-full object-cover" />
          </div>

          <div className="lg:col-span-6 flex flex-col justify-center">
            <h3 className="font-serif-luxury text-3xl md:text-4xl text-[#0F4C3A] leading-tight">
              Gower Golf Club
              <span className="italic text-gold-metallic"> Merchandise.</span>
            </h3>
            <p className="text-[#4A4A4A] mt-5 leading-relaxed max-w-xl">
              Browse the full Gower Golf Club merchandise catalogue online. From golf balls and tees to accessories and club merchandise,
              our Pro Shop offers a selection of essentials for members and visitors alike.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                data-testid="shop-online-btn"
                href="https://pro-shop-108676.square.site/s/shop"
                target="_blank"
                rel="noopener noreferrer"
                className="ggc-btn-primary group"
              >
                Shop Merchandise Online
                <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>

            <form onSubmit={onSubmit} className="hidden mt-10 bg-white border border-[#CBA153]/30 p-7" data-testid="lessons-form">
              <p className="section-eyebrow">Book Golf Lessons</p>
              <h4 className="font-serif-luxury text-xl text-[#0F4C3A] mt-2 mb-5">PGA Tuition Inquiry</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="luxury-label" htmlFor="lesson-name">Name</label>
                  <input id="lesson-name" data-testid="lesson-input-name" className="luxury-input" value={form.name} onChange={update("name")} required />
                </div>
                <div>
                  <label className="luxury-label" htmlFor="lesson-email">Email</label>
                  <input id="lesson-email" data-testid="lesson-input-email" type="email" className="luxury-input" value={form.email} onChange={update("email")} required />
                </div>
                <div>
                  <label className="luxury-label" htmlFor="lesson-phone">Phone</label>
                  <input id="lesson-phone" data-testid="lesson-input-phone" type="tel" className="luxury-input" value={form.phone} onChange={update("phone")} />
                </div>
                <div>
                  <label className="luxury-label" htmlFor="lesson-msg">Brief Goals</label>
                  <input id="lesson-msg" data-testid="lesson-input-msg" className="luxury-input" placeholder="e.g. short game, fitting" value={form.message} onChange={update("message")} />
                </div>
              </div>
              <button data-testid="lesson-submit-btn" type="submit" disabled={loading} className="ggc-btn-gold mt-7 w-full sm:w-auto disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={16} /> : "Request Lesson"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
